/**
 * G.1 — SharePoint-backed VaultSaveProvider transport.
 *
 * Wires the existing save-provider machinery (lib/vault/saveProvider.ts)
 * to the live Graph adapter (lib/graph/sharepoint.ts). The transport
 * implements the `PutTransport` interface so the SPA can swap it in
 * place of `createInMemoryTransport` without touching the rest of the
 * provider state machine.
 *
 * Responsibilities:
 *   - PUT a note body via `putContent(driveId, itemId, body, etag)`
 *     with the existing WRITE_POLICY retry + IDB-queue fallback.
 *   - Map 412/409 from `putContent` into a `ConflictResult` so the
 *     existing ConflictDialog surfaces the diff.
 *   - Fetch the current server-side body for the conflict resolver
 *     using `getContent(driveId, itemId)` + a text decode.
 *
 * Path → externalRef resolution is handed in by the SPA at construction
 * time. Live `vaultNotes` rows carry `externalRef = {siteId, driveId,
 * itemId, webUrl}`; the SPA builds the map from those rows.
 */

import { getContent, putContent } from '../graph/sharepoint';
import type { FlushOutcome, PutTransport } from './saveProvider';

export interface SharePointNoteRef {
  driveId: string;
  itemId: string;
  /** Most recent server-side etag for the note. */
  etag: string;
}

export interface SharePointTransportOptions {
  /**
   * Returns the live driveId / itemId / etag for a vault-relative path,
   * or null when the note doesn't have an externalRef yet (vault-only
   * notes that were never pushed to SP, or live rows that arrived via
   * a different source). The provider falls back to a no-op `saved`
   * result for null refs so editing a local-only note doesn't error.
   */
  resolveRef(notePath: string): SharePointNoteRef | null;
  /**
   * Called after a successful PUT so the SPA can patch Convex with the
   * new etag. Best-effort — errors are swallowed and toasted.
   */
  onEtagBump?: (notePath: string, newEtag: string) => void;
}

const TEXT_DECODER_FALLBACK = new TextDecoder('utf-8');

function decodeArrayBuffer(buf: ArrayBuffer): string {
  return TEXT_DECODER_FALLBACK.decode(new Uint8Array(buf));
}

/**
 * 412 / 409 are the only conflict statuses Graph raises on If-Match
 * mismatches. Any HTTP-shaped error with a statusCode in this set is
 * treated as a conflict; everything else surfaces as a SaveError.
 */
function isConflictError(err: unknown): boolean {
  const e = err as { statusCode?: number; code?: string };
  if (e?.statusCode === 412 || e?.statusCode === 409) return true;
  if (e?.code === 'preconditionFailed' || e?.code === 'resourceModified') return true;
  return false;
}

export function createSharePointTransport(opts: SharePointTransportOptions): PutTransport {
  return {
    async put(options): Promise<FlushOutcome> {
      const ref = opts.resolveRef(options.notePath);
      if (!ref) {
        // Local-only note. Treat as an in-memory save so the editor's
        // dirty badge clears; the SPA can promote the note to SP via
        // F3's pending-writes tray on demand.
        return {
          status: 'saved',
          newEtag: options.expectedEtag ?? `local-${Date.now()}`,
          savedAt: Date.now(),
        };
      }
      const etagToSend = options.expectedEtag ?? ref.etag;
      try {
        const updated = await putContent(
          ref.driveId,
          ref.itemId,
          options.markdown,
          etagToSend,
        );
        const newEtag = updated.eTag ?? '';
        if (!newEtag) {
          return {
            status: 'error',
            message: 'Graph returned no etag after PUT — refusing to advance saveState.',
          };
        }
        try {
          opts.onEtagBump?.(options.notePath, newEtag);
        } catch {
          /* etag patch is best-effort; UI already has the new value */
        }
        return { status: 'saved', newEtag, savedAt: Date.now() };
      } catch (err) {
        if (isConflictError(err)) {
          // Pull the remote body so the ConflictDialog can render the
          // diff. Falls back to an empty string when getContent itself
          // fails — the dialog handles missing remoteContent.
          let remoteContent = '';
          let remoteEtag = '';
          try {
            const buf = await getContent(ref.driveId, ref.itemId);
            remoteContent = decodeArrayBuffer(buf);
          } catch {
            /* swallow — the user can still resolve manually */
          }
          // Best-effort etag re-fetch via a fresh ref lookup. The SPA's
          // map is updated when listNotesByVault refreshes; resolveRef
          // is the source of truth.
          const refreshed = opts.resolveRef(options.notePath);
          remoteEtag = refreshed?.etag ?? ref.etag;
          return {
            status: 'conflict',
            serverBody: remoteContent,
            serverEtag: remoteEtag,
          };
        }
        const message = err instanceof Error ? err.message : String(err);
        return { status: 'error', message };
      }
    },
    async fetchCurrent(notePath): Promise<{ body: string; etag: string } | null> {
      const ref = opts.resolveRef(notePath);
      if (!ref) return null;
      try {
        const buf = await getContent(ref.driveId, ref.itemId);
        return { body: decodeArrayBuffer(buf), etag: ref.etag };
      } catch {
        return null;
      }
    },
  };
}
